// src/pages/ContinentsPage.js
import React, { useState, useEffect } from 'react';
import styles from './ContinentsPage.module.css';
import apiClient from '../services/ApiClient';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusToggle from '../components/common/StatusToggle';
import { toast } from 'react-toastify';

const ContinentsPage = () => {
    const [continents, setContinents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [savingStates, setSavingStates] = useState({});

    // Kıta verilerini API'den çek
    const fetchContinents = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/continents');
            
            if (response.data && response.data.status === 'success' && Array.isArray(response.data.data)) {
                // API sırası rastgele geliyor — ID'ye göre sabitle
                setContinents([...response.data.data].sort((a, b) => a.id - b.id));
            } else {
                throw new Error("API'den beklenen formatta veri gelmedi.");
            }
        } catch (err) {
            console.error('Kıtalar yüklenirken hata:', err);
            setError('Kıtalar yüklenirken bir hata oluştu.');
            setContinents([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchContinents();
    }, []);

    const handleToggleStatus = async (continentId, statusType, currentStatus) => {
        const stateKey = `${continentId}-${statusType}`;
        setSavingStates(prev => ({ ...prev, [stateKey]: true }));
        
        try {
            const newStatus = !currentStatus;
            
            // Optimistic update
            setContinents(prevContinents =>
                prevContinents.map(continent =>
                    continent.id === continentId
                        ? { ...continent, [statusType]: newStatus }
                        : continent
                )
            );

            // API'ye güncelleme gönder
            await apiClient.patch(`/continents/${continentId}`, {
                [statusType]: newStatus
            });

            const continentName = continents.find(c => c.id === continentId)?.name;
            const statusText = statusType === 'isActiveLifetime' ? 'Ömürlük ESim' : 'Kıtalar Arası ESim';
            toast.success(`${continentName} kıtası ${statusText} paketleri ${newStatus ? 'aktif' : 'pasif'} yapıldı.`);

        } catch (err) {
            console.error('Kıta durumu güncellenirken hata:', err);
            toast.error('Kıta durumu güncellenirken bir hata oluştu.');
            
            // Hata durumunda geri al
            setContinents(prevContinents =>
                prevContinents.map(continent =>
                    continent.id === continentId
                        ? { ...continent, [statusType]: currentStatus }
                        : continent
                )
            );
        } finally {
            setSavingStates(prev => ({ ...prev, [stateKey]: false }));
        }
    };

    const activeLifetimeCount = continents.filter(c => c.isActiveLifetime).length;
    const activeIntercontinentalCount = continents.filter(c => c.isActiveIntercontinental).length;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>
                        Kıtalar Yönetimi
                        {!isLoading && !error && <span className={`${styles.count} num`}>{continents.length}</span>}
                    </h1>
                    <p className={styles.subtitle}>
                        Kıta bazında eSIM paket yayınını yönetin. Anahtarı açtığınızda değişiklik anında kaydedilir.
                    </p>
                </div>
            </div>

            {isLoading && (
                <div className={styles.loadingContainer}>
                    <LoadingSpinner />
                </div>
            )}

            {!isLoading && error && (
                <div className={styles.errorPanel}>
                    <p className={styles.errorPanelText}>Veriler alınamadı. Bağlantıyı kontrol edip tekrar deneyin.</p>
                    <button type="button" onClick={fetchContinents} className={`${styles.retryButton} ghost`}>
                        Tekrar dene
                    </button>
                </div>
            )}

            {!isLoading && !error && (
            <div className={styles.contentArea}>
                <div className={styles.statsContainer}>
                    <div className={styles.statCard}>
                        <h3>Toplam Kıta</h3>
                        <p className={styles.statNumber}>{continents.length}</p>
                    </div>
                    <div className={styles.statCard}>
                        <h3>Ömürlük ESim Aktif</h3>
                        <p className={styles.statNumber}>
                            {activeLifetimeCount}<span className={styles.statTotal}>/{continents.length}</span>
                        </p>
                    </div>
                    <div className={styles.statCard}>
                        <h3>Kıtalar Arası Aktif</h3>
                        <p className={styles.statNumber}>
                            {activeIntercontinentalCount}<span className={styles.statTotal}>/{continents.length}</span>
                        </p>
                    </div>
                </div>

                <div className={styles.tableContainer}>
                    <table className={styles.continentsTable}>
                        <thead>
                            <tr>
                                <th className={styles.idHead}>ID</th>
                                <th className={styles.nameHead}>Kıta Adı</th>
                                <th>Ömürlük ESim Paketleri</th>
                                <th>Kıtalar Arası ESim Paketleri</th>
                            </tr>
                        </thead>
                        <tbody>
                            {continents.map(continent => (
                                <tr key={continent.id} className={styles.tableRow}>
                                    <td className={styles.idCol}><span className={`${styles.idCell} num`}>{continent.id}</span></td>
                                    <td className={styles.nameCell}>{continent.name}</td>
                                    <td className={styles.statusCell}>
                                        <StatusToggle
                                            label={`${continent.name} — Ömürlük ESim paketleri`}
                                            checked={continent.isActiveLifetime}
                                            saving={savingStates[`${continent.id}-isActiveLifetime`]}
                                            onChange={() => handleToggleStatus(continent.id, 'isActiveLifetime', continent.isActiveLifetime)}
                                        />
                                    </td>
                                    <td className={styles.statusCell}>
                                        <StatusToggle
                                            label={`${continent.name} — Kıtalar arası ESim paketleri`}
                                            checked={continent.isActiveIntercontinental}
                                            saving={savingStates[`${continent.id}-isActiveIntercontinental`]}
                                            onChange={() => handleToggleStatus(continent.id, 'isActiveIntercontinental', continent.isActiveIntercontinental)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            )}
        </div>
    );
};

export default ContinentsPage; 